/*
Copyright (c) 2026 Wind River Systems, Inc.

The right to copy, distribute, modify, or otherwise make use of this software may be licensed only pursuant to the terms of an applicable Wind River license agreement.
*/
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { environment } from '@environment';
import { ApiResponse, SearchUserResult } from '@models';
import { PageEvent } from '@angular/material/paginator';
import { finalize, flatMap, map, switchMap } from 'rxjs/operators';
import { of } from 'rxjs';
import { HttpEventType, HttpResponse } from '@angular/common/http';
// components
import { ConfirmModalComponent } from '@windriver/ui';
import { LicenseAgreementComponent } from './license-agreement/license-agreement.component';
import { OrderDetailsComponent } from './order-details/order-details.component';
import { OfflineInstallHistoryComponent } from './offline-install-history/offline-install-history.component';
import { ReleaseNotesComponent } from './release-notes/release-notes.component';
import { SysNetRequirementsComponent } from './sys-net-requirements/sys-net-requirements.component';
import { SearchCustomersComponent } from './search-customers/search-customers.component';
import { RecentCustomersComponent } from './recent-customers/recent-customers.component';
// services
import { ConfigService } from '@windriver/authentication';
import { CurrentUserService } from 'src/app/services/current-user/current-user.service';
import { RefreshService } from 'src/app/services/refresh/refresh.service';
import { ProductsService } from 'src/app/services/products/products.service';
import {
  CommItem,
  DownloadHistory,
  EntitledRelease,
  Entitlement,
  Product,
  ProductArtifact,
  Release
} from 'src/app/models/products';
import { UiCommItem, UiFolder, UiProduct, UiRelease } from './products.models';

@Component({
  selector: 'products',
  templateUrl: './products.component.html',
  styleUrls: ['./products.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProductsComponent implements OnInit, OnDestroy {
  public harborUrl: string;
  public galleryUrl: string;
  public minioUrl: string;
  public gitlabUrl: string;
  private apiBackendBaseUrl: string;
  // This URL is no loner supported
  //readonly esdCenterUrl = environment.esdCenterUrl;
  readonly installLicenseUrl = environment.installLicenseUrl;
  readonly enableOfflineInternal = environment.enableOfflineInternal;
  readonly enableOfflineExternal = environment.enableOfflineExternal;
  readonly disabledProducts = environment.disabledProducts;
  public isInternal = false;

  //  === These items are parts of the ui that is only for internal users ===
  public allProducts: Product[]; // All available products
  public allReleases: Release[]; // All alvailable releases for the selected product
  public commItems: CommItem[]; // All comm items for the selected product and release
  // Pagination of comm items display
  public commItemsPage: CommItem[];
  public commItemsPageNumber: number;
  public readonly commItemsPageSize = 25;
  // Pagination of selected customer comm items display
  public selectedCustomerCommItemsPage: CommItem[];
  public selectedCustomerCommItemsPageNumber: number;
  public readonly selectedCustomerCommItemsPageSize = 10;
  // Current selections
  public selectedReleaseType: 'release' | 'ear' | 'spin' = 'release';
  public selectedProduct: Product;
  public selectedRelease: Release | null;
  public selectedCustomer: SearchUserResult;
  public selectedCustomerCommItems: CommItem[];
  public offlineInstallerType: string;
  public offlineInstallerVersions: string[];
  // If defined, the customer being impersonated by the internal user
  public impersonatedUserName: string = undefined;
  public hasImpersonationPermission: boolean = undefined;
  // tools sync result
  public toolSyncStatus = false;
  public toolSyncResult: ApiResponse;
  // Spinner ngIf flags
  public loadingSelectedCustomerCommItems = false;
  public loadingProducts = false;
  public loadingReleases = false;
  public loadingCommItems = false;
  public addingCommItems = false;
  public refreshingEntitlements = false;
  public startingImpersonation = false;
  public stoppingImpersonation = false;
  public startToolSync = false;

  // === These are for customer facing ui elements ===
  public artifacts: ProductArtifact[];
  public entitledProducts: UiProduct[] = [];
  public loadingEntitlements = false;
  public entitlementsError = false;
  public isDeletingProduct = [];
  public loadingOnlineInstaller: boolean[] = [];
  public loadingOnlineInstallerProgress: number[] = [];
  public loadingReleaseVersions = false;
  public loadingOfflineInstaller = false;
  public requestingOfflineInstaller: boolean[] = [];
  public offlineInstallerStatus = new Map<string, string>();
  private pollingIntervals = new Map<string, ReturnType<typeof setInterval>>();
  private readonly pageOpenTime = Date.now();
  private dialogRef: MatDialogRef<OrderDetailsComponent>;

  constructor(
    private productService: ProductsService,
    private dialog: MatDialog,
    private detector: ChangeDetectorRef,
    private refreshService: RefreshService,
    private snackBar: MatSnackBar,
    private configService: ConfigService,
    public currentUserService: CurrentUserService
  ) {}

  ngOnInit(): void {
    this.configService.configState.subscribe((config: any) => {
      if (config) {
        this.harborUrl = config.harborUrl;
        this.galleryUrl = config.galleryUrl;
        this.minioUrl = config.minioUrl;
        this.gitlabUrl = config.gitlabUrl;
        this.apiBackendBaseUrl = config.apiUrl;
      }
    });

    if (!this.currentUserService.isInternal() && !this.currentUserService.isExternal()) {
      this.currentUserService.getUserRole().subscribe((response) => {
        this.isInternal = response.data === 'Internal';
      });
    } else {
      this.isInternal = this.currentUserService.isInternal();
    }
    this.hasImpersonationPermission = this.currentUserService.hasImpersonate();

    this.loadingOfflineInstaller = this.isInternal ? this.enableOfflineInternal : this.enableOfflineExternal;
    this.loadAllProducts();

    this.offlineInstallerType = 'zip';
    this.loadEntitledProducts(false);
    if (this.currentUserService.isAdmin()) {
      this.onSync();
    }
  }

  private loadAllProducts() {
    if (this.isInternal) {
      this.loadingProducts = true;
      this.productService.getProducts().subscribe((p) => {
        if (p.length > 0) {
          this.allProducts = p;
        } else {
          this.allProducts = [];
        }
        this.loadingProducts = false;
        this.detector.detectChanges();
      });
    }
  }

  private loadEntitledProducts(refreshEntitlementsFirst: boolean) {
    this.loadingEntitlements = true;
    this.entitlementsError = false;
    this.entitledProducts = [];

    //Start by looking at the refresh flag
    of(refreshEntitlementsFirst)
      .pipe(
        switchMap((doRefresh) => {
          if (doRefresh) {
            //Only refresh if the flag is true and update the UI
            return this.refreshService.RefreshEverything(this.currentUserService.getUsername()).pipe(
              finalize(() => {
                this.refreshingEntitlements = false;
                this.detector.detectChanges();
              })
            );
          } else {
            //Blank value for the "do not refresh" path
            return of('');
          }
        })
      )
      .pipe(
        //flatMap because we don't care about the result of the success case for refreshing entitlements
        // flatMap((output) => of(this.currentUserService.isInternal())),
        flatMap(() => {
          if (this.isInternal) {
            //If the user is internal, we call the get impersonation endpoint and pass the impersonated username forward
            return this.productService.getImpersonation(this.currentUserService.getUsername()).pipe(
              map((result) => {
                this.impersonatedUserName = result.impersonatedCustomerUserName;
                return result.impersonatedCustomerUserName;
              })
            );
          }
          // Otherwise return external user's username to load their entitlements
          else {
            return of(this.currentUserService.getUsername());
          }
        }),
        flatMap(() =>
          this.productService.getUserEntitledReleases(this.currentUserService.getUsername(), this.impersonatedUserName)
        )
      )
      .subscribe(
        (e) => {
          this.populateEntitledProducts(e);
          this.loadingEntitlements = false;
          this.detector.detectChanges();
        },
        (err) => {
          this.loadingEntitlements = false;
          this.entitlementsError = true;
          console.error('ERROR! ', err);
          this.detector.detectChanges();
        },
        () => {}
      );
  }

  // Convert the flat REST response into heirachical representation of products->releases->comm_items.
  private populateEntitledProducts(entitlements: EntitledRelease[]): void {
    this.entitledProducts = [];

    const products = new Map(); // key = product id, value = product data

    for (const entitlement of entitlements) {
      // add product if not present
      let uiProduct: UiProduct = products.get(entitlement.product.id);
      if (uiProduct === undefined) {
        uiProduct = {
          id: entitlement.product.id,
          name: entitlement.product.name,
          eng_name: entitlement.product.eng_name,
          includes_container_registry: entitlement.is_container,
          releases: []
        };
        products.set(uiProduct.id, uiProduct);
      }
      // add release
      const cardKey = `${entitlement.id}-${entitlement.release_eng_name_tag_eng_name || 0}`;
      const uiRelease: UiRelease = {
        id: entitlement.id,
        cardKey,
        name: entitlement.release_eng_name_tag_comm_name || entitlement.name,
        releaseName: entitlement.name,
        release_eng_name_tag_eng_name: entitlement.release_eng_name_tag_eng_name,
        eng_name: entitlement.eng_name,
        isDisabled: this.disabledProducts.indexOf(entitlement.eng_name) > -1,
        is_folder: null,
        is_git: null,
        git_clone_Url: this.apiBackendBaseUrl,
        gitlab_eng_repo_Url: this.gitlabUrl,
        gitlab_repo_Url: this.gitlabUrl,
        git_clone_folder_name: null,
        git_clone_release_branch_name: null,
        release_documentation_link: null,
        sample_command_folder_name: entitlement.eng_name === 'wrlinux-9' ? 'wrlinux-9' : 'wrlinux-x',
        has_notes: entitlement.has_notes,
        release_notes: {
          name: entitlement.name,
          notes: []
        },
        license_agreement: null,
        folders: [],
        offline_request_logs: null,
        comm_items: [],
        installation_technology: entitlement.installation_technology,
        all_comm_items_expired: true,
        versions: [],
        detailsState: 'not-loaded'
      };
      uiProduct.releases.push(uiRelease);
    }

    // Disambiguate: if two cards share the same name, prefix each with its release name
    const nameCounts = new Map<string, number>();
    for (const product of products.values()) {
      for (const rel of product.releases) {
        nameCounts.set(rel.name, (nameCounts.get(rel.name) ?? 0) + 1);
      }
    }
    for (const product of products.values()) {
      for (const rel of product.releases) {
        if ((nameCounts.get(rel.name) ?? 0) > 1) {
          rel.name = `${rel.releaseName} - ${rel.name}`;
        }
      }
    }

    this.entitledProducts = Array.from(products.values());
    this.detector.detectChanges();
    this.entitledProducts.forEach((p) => p.releases.forEach((r) => this.startStatusPolling(r)));
  }

  private populateEntitedReleaseDetails(releaseDatails: Entitlement[], uiRelease: UiRelease): void {
    //const minIoBaseUrl = this.minioUrl?.replace('console.', '').replace('browser/', '');
    if (releaseDatails.length > 0) {
      uiRelease.is_folder = releaseDatails[0].is_folder;
      uiRelease.is_git = releaseDatails[0].is_git;
      uiRelease.git_clone_folder_name = releaseDatails[0].git_clone_folder_name;
      uiRelease.git_clone_release_branch_name = releaseDatails[0].git_clone_release_branch_name;
      uiRelease.release_documentation_link = releaseDatails[0].release_documentation_link;
      uiRelease.license_agreement = {
        accepted: releaseDatails[0].release.license_agreement.accepted,
        url: releaseDatails[0].release.license_agreement.url, // this.minioUrl + releaseDatails[0].release.license_agreement.url,
        url_id: releaseDatails[0].release.license_agreement.url_id
      };
      uiRelease.offline_request_logs = releaseDatails[0].release.offline_request_logs;
    }
    for (const entitlement of releaseDatails) {
      // Create a temp comm item
      const tempCi: UiCommItem = {
        id: entitlement.id,
        comm_item_id: entitlement.comm_item_id,
        description: entitlement.description,
        is_git: entitlement.is_git,
        is_container: entitlement.is_container,
        is_folder: entitlement.is_folder,
        expiration_date: entitlement.expiration_date,
        children: entitlement.children as UiCommItem[]
      };
      if (this.isExpired(entitlement.expiration_date)) {
        // Expired comm items better show up after the non-expired ones in the order details expansion panel.
        // Hence, adding the expired comm item to the end of the comm items list.
        uiRelease.comm_items.push(tempCi);
      } else {
        // The all_comm_items_expired defaults to true for each release object.
        // If only one of the release comm items is not expired, the all_comm_items_expired should be updated to false
        if (uiRelease.all_comm_items_expired) {
          uiRelease.all_comm_items_expired = false;
        }
        // Add the folders to release only if the comm_item is not expired
        // record appended folder to prevent dup
        const folderList = [];
        if (uiRelease.folders?.length > 0) {
          uiRelease.folders.forEach((element) => {
            const [folderName] = Object.values(element);
            folderList.push(folderName);
          });
        }

        for (const f of entitlement.folders) {
          const [name] = Object.keys(f);
          const folder: UiFolder = {
            name,
            path: this.minioUrl + f[name] + '/'
          };

          // ignore already appended folder
          if (!folderList.includes(name)) {
            uiRelease.folders.push(folder);
            folderList.push(name);
          }
        }

        // Folders belonging to all child comm_items need to be also added to release folders
        for (const child of entitlement.children) {
          for (const childFolder of child.folders) {
            const [name] = Object.keys(childFolder);
            const childFolderObject: UiFolder = {
              name,
              path: this.minioUrl + childFolder[name] + '/'
            };
            // ignore already appended folder
            if (!folderList.includes(name)) {
              uiRelease.folders.push(childFolderObject);
              folderList.push(name);
            }
          }
        }
        // Add the comm item to the beginning of the comm items list if not expired
        uiRelease.comm_items.unshift(tempCi);
      }
      uiRelease.git_clone_folder_name = entitlement.git_clone_folder_name ?? uiRelease.git_clone_folder_name;
      uiRelease.git_clone_release_branch_name =
        entitlement.git_clone_release_branch_name ?? uiRelease.git_clone_release_branch_name;
      uiRelease.release_documentation_link =
        entitlement.release_documentation_link ?? uiRelease.release_documentation_link;

      // Build git clone and gitlab URLs from the resolved folder path
      // The folder path from Redis already contains the correct structure for both GA and prerelease
      if (entitlement.folders?.length > 0) {
        const firstFolderPath = Object.values(entitlement.folders[0])[0] as string;
        // firstFolderPath is e.g. "linux-lts/release/wrlinux-lts.24/WRLinux-lts-24-Core"
        // or "linux-lts/prerelease/wrlinux-lts.24-dev/wrlinux-lts.24/WRLinux-lts-24-Core"
        // Strip the last segment (folder name) to get the release path prefix
        const releasePathPrefix = firstFolderPath.substring(0, firstFolderPath.lastIndexOf('/'));

        uiRelease.git_clone_Url =
          this.apiBackendBaseUrl +
          '/git/' +
          releasePathPrefix +
          '/' +
          uiRelease.git_clone_folder_name +
          '/' +
          uiRelease.sample_command_folder_name;

        uiRelease.gitlab_eng_repo_Url = this.gitlabUrl + releasePathPrefix;
        uiRelease.gitlab_repo_Url =
          uiRelease.gitlab_eng_repo_Url +
          '/' +
          uiRelease.git_clone_folder_name +
          '/' +
          uiRelease.sample_command_folder_name +
          '/-/tree/' +
          uiRelease.git_clone_release_branch_name;
      }
    }
    uiRelease.detailsState = 'loaded';
    this.detector.detectChanges();
  }

  private _filteredReleases: Release[] = [];
  private _lastReleaseType = '';
  private _lastAllReleases: Release[] = [];

  get filteredReleases(): Release[] {
    if (this._lastReleaseType === this.selectedReleaseType && this._lastAllReleases === this.allReleases) {
      return this._filteredReleases;
    }
    this._lastReleaseType = this.selectedReleaseType;
    this._lastAllReleases = this.allReleases;
    if (!this.allReleases) {
      this._filteredReleases = [];
    } else {
      this._filteredReleases = this.allReleases.filter((r) => {
        const isWsodOnly = r.name.includes('[WSoD Only]');
        const nameLower = r.name.toLowerCase();
        const isReleased = nameLower.includes('released');
        const isUnderDev = nameLower.includes('under dev');
        switch (this.selectedReleaseType) {
          case 'release':
            return isReleased && !isWsodOnly;
          case 'ear':
            return !isReleased && !isUnderDev && !isWsodOnly;
          case 'spin':
            return isUnderDev && !isWsodOnly;
        }
      });
    }
    return this._filteredReleases;
  }

  onSelectReleaseType(type: 'release' | 'ear' | 'spin'): void {
    this.selectedReleaseType = type;
    this.selectedRelease = null;
    this.commItems = [];
    this.commItemsPage = [];
    this.loadingReleases = true;
    this.detector.detectChanges();
    setTimeout(() => {
      this.loadingReleases = false;
      this.detector.detectChanges();
    });
  }

  onSelectProduct(product: Product): void {
    this.selectedProduct = product;
    this.selectedRelease = null;
    this.loadingReleases = true;
    this.productService.getReleases(product).subscribe((r) => {
      this.allReleases = r;
      this.loadingReleases = false;
      this.detector.detectChanges();
    });
  }

  onSelectRelease(release: Release): void {
    this.selectedRelease = release;
    this.loadingCommItems = true;
    this.productService.getCommItems(this.selectedProduct, this.selectedRelease).subscribe((commItems) => {
      this.commItems = commItems;
      this.commItemsPage = commItems.slice(0, this.commItemsPageSize);
      this.commItemsPageNumber = 0;
      this.loadingCommItems = false;
      this.detector.detectChanges();
    });
    this.productService.getArtifacts().subscribe((a) => (this.artifacts = a));
  }

  handleCommItemsPageEvent(event: PageEvent) {
    this.commItemsPageNumber = event.pageIndex;
    const start = this.commItemsPageNumber * this.commItemsPageSize;
    const end = start + this.commItemsPageSize;
    this.commItemsPage = this.commItems.slice(start, end);
  }

  handleSelectedCustomerCommItemsPageEvent(event: PageEvent) {
    this.selectedCustomerCommItemsPageNumber = event.pageIndex;
    const start = this.selectedCustomerCommItemsPageNumber * this.selectedCustomerCommItemsPageSize;
    const end = start + this.selectedCustomerCommItemsPageSize;
    this.selectedCustomerCommItemsPage = this.selectedCustomerCommItems.slice(start, end);
  }

  onAdd(): void {
    this.addingCommItems = true;
    this.productService
      .addtoMyProducts(this.currentUserService.getUsername(), this.commItems, this.selectedRelease)
      .subscribe(() => {
        this.addingCommItems = false;
        this.loadEntitledProducts(true);
      });
  }

  retryLoadEntitlements(): void {
    this.loadEntitledProducts(false);
  }

  onRefresh(): void {
    this.loadEntitledProducts(true);
  }

  onSync(): void {
    this.startToolSync = true;
    this.refreshService.SyncTools(this.currentUserService.getUsername()).subscribe(
      (r) => {
        this.toolSyncStatus = r.sync ?? true;
        this.toolSyncResult = r;
        this.startToolSync = false;
        this.detector.detectChanges();
      },
      (err) => {
        this.toolSyncStatus = err.sync ?? false;
        this.toolSyncResult = err;
        this.startToolSync = false;
        this.detector.detectChanges();
      },
      () => {}
    );
  }

  private loadEntitledReleaseDetails(release: UiRelease) {
    release.detailsState = 'loading';
    this.productService
      .getUserEntitledReleaseDetails(
        release.id,
        this.currentUserService.getUsername(),
        this.impersonatedUserName,
        release.release_eng_name_tag_eng_name
      )
      .subscribe(
        (releaseDetails: Entitlement[]) => {
          this.populateEntitedReleaseDetails(releaseDetails, release);
          release.detailsState = 'loaded';
          if (this.dialogRef && this.dialogRef.componentInstance) {
            this.dialogRef.componentInstance.ngOnInit();
          }
        },
        (err) => {
          release.detailsState = 'not-loaded';
          console.error('ERROR! ', err);
          this.snackBar.open('The Entitlement details operation was not successful', 'Close');
        },
        () => {}
      );
  }

  onExpandReleasePanel(release: UiRelease) {
    if (release.detailsState === 'not-loaded') {
      this.loadEntitledReleaseDetails(release);
    }
    if (release.installation_technology === 'RPM' && !release.release_eng_name_tag_eng_name) {
      this.loadingReleaseVersions = true;
      this.productService.getReleaseVersions(release.id).subscribe((rv) => {
        release.versions = rv;
        release.selectedVersion = rv[0];
        this.loadingReleaseVersions = false;
        this.detector.detectChanges();
      });
    }
  }

  private isOfflineEligible(release: UiRelease): boolean {
    return (
      (release.installation_technology === 'RPM' ||
        release.installation_technology === 'GIT_CLONE' ||
        release.installation_technology === 'GIT') &&
      !release.release_eng_name_tag_eng_name
    );
  }

  displayLicenseAgreement(event: Event, uiRelease: UiRelease): void {
    this.dialog
      .open(LicenseAgreementComponent, { data: uiRelease })
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          this.productService
            .acceptLicenseAgreement(
              this.currentUserService.getUsername(),
              uiRelease.id,
              uiRelease.license_agreement.url_id
            )
            .subscribe(() => {
              uiRelease.license_agreement.accepted = true;
              this.detector.detectChanges();
            });
        }
      });
    event.stopPropagation();
  }

  onDeleteRelease(releaseId: number, releaseName: string, cardKey?: string, engNameTag?: string) {
    const key = cardKey ?? String(releaseId);
    this.isDeletingProduct[key] = true;
    this.dialog
      .open(ConfirmModalComponent, {
        data: {
          title: `Are you sure you want to remove ${releaseName}?`,
          message: 'Everything listed under Order Details will be removed from your account.',
          confirmButton: 'Remove',
          showConfirmButton: true,
          dismissButton: 'Cancel',
          showDismissButton: true
        }
      })
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          this.showBackgroundMessageOnDelete();
          this.productService
            .removeReleaseFromMyProducts(this.currentUserService.getUsername(), releaseId, engNameTag)
            .subscribe(() => {
              this.loadEntitledProducts(true);
              this.isDeletingProduct[key] = false;
              this.detector.detectChanges();
            });
        } else {
          this.isDeletingProduct[key] = false;
          this.detector.detectChanges();
        }
      });
  }

  showBackgroundMessageOnDelete() {
    let counter = 5;
    const buildData = () => ({
      title: ' ',
      message: `Removing the product in the background... \n Once the tool is removed, the page will refresh. (${counter}s)`,
      confirmButton: `Ok`,
      showConfirmButton: true,
      showDismissButton: false
    });

    const dialog = this.dialog.open(ConfirmModalComponent, {
      data: buildData()
    });

    const reduce = () => {
      setTimeout(() => {
        counter -= 1;
        dialog.componentInstance.message = buildData().message;

        if (counter > 0) {
          reduce();
          return;
        }

        dialog.close();
      }, 1000);
    };

    reduce();
  }

  getProductName(productId: number): string {
    return this.entitledProducts.find((e) => e.id === productId).name;
  }

  openOrderDetails(event: Event, uiRelease: UiRelease): void {
    if (uiRelease.detailsState === 'not-loaded') {
      this.loadEntitledReleaseDetails(uiRelease);
    }
    this.dialogRef = this.dialog.open(OrderDetailsComponent, { data: uiRelease });
    event.stopPropagation();
  }

  openReleaseNotes(event: Event, uiRelease: UiRelease): void {
    this.dialog.open(ReleaseNotesComponent, {
      data: uiRelease
    });
    event.stopPropagation();
  }

  openSysNetRequirements(): void {
    this.dialog.open(SysNetRequirementsComponent);
  }

  openSearchCustomers(): void {
    this.dialog
      .open(SearchCustomersComponent)
      .afterClosed()
      .subscribe((selectedCustomer) => this.selectCustomer(selectedCustomer));
  }

  openRecentCustomers(): void {
    this.dialog
      .open(RecentCustomersComponent)
      .afterClosed()
      .subscribe((selectedCustomer) => this.selectCustomer(selectedCustomer));
  }

  selectCustomer(customer: SearchUserResult): void {
    if (customer.user_id) {
      this.selectedCustomer = customer;
      this.selectedCustomerCommItems = [];
      this.selectedCustomerCommItemsPage = [];
      this.loadingSelectedCustomerCommItems = true;
      this.detector.detectChanges();
      this.productService.getCustomerCommItems(customer.user_id).subscribe((commItems) => {
        this.selectedCustomerCommItems = commItems;
        this.selectedCustomerCommItemsPage = commItems.slice(0, this.selectedCustomerCommItemsPageSize);
        this.loadingSelectedCustomerCommItems = false;
        this.detector.detectChanges();
      });
    }
  }

  getImpersonation() {
    this.productService.getImpersonation(this.currentUserService.getUsername()).subscribe((result) => {
      this.impersonatedUserName = result.impersonatedCustomerUserName;
    });
  }

  impersonateUser(): void {
    this.startingImpersonation = true;
    this.productService
      .startImpersonation(this.currentUserService.getUsername(), this.selectedCustomer.user_id)
      .subscribe((result) => {
        this.startingImpersonation = false;
        this.toolSyncStatus = result.sync ?? false;
        if (result.status === 'success' || typeof result.sync !== 'undefined') {
          this.impersonatedUserName = this.selectedCustomer.user_id;
          this.loadEntitledProducts(true);
          this.openModalImpersonation(this.selectedCustomer.user_id);
        } else {
          console.log('Failed to start impersonation', result);
          this.snackBar.open('Failed to start impersonation', 'Close');
        }
      });
  }

  stopImpersonatingUser(): void {
    this.stoppingImpersonation = true;
    this.productService.stopImpersonation(this.currentUserService.getUsername()).subscribe((result) => {
      this.stoppingImpersonation = false;
      this.toolSyncStatus = result.sync ?? false;
      if (result.status === 'success' || typeof result.sync !== 'undefined') {
        this.impersonatedUserName = undefined;
        this.selectedCustomerCommItemsPage = [];
        this.selectedCustomer = null;
        this.loadEntitledProducts(true);
      } else {
        console.log('Failed to stop impersonation', result);
        this.snackBar.open('Failed to stop impersonation', 'Close');
      }
    });
  }

  onArchiveChoiceChange(offlineInstallerType: string) {
    this.offlineInstallerType = offlineInstallerType;
  }

  generateOfflineInstaller(release: UiRelease) {
    this.requestingOfflineInstaller[release.id] = true;
    const isGit = release.installation_technology === 'GIT_CLONE' || release.installation_technology === 'GIT';
    this.productService
      .getOfflineInstaller(
        this.currentUserService.getUsername(),
        release.eng_name,
        this.offlineInstallerType,
        release.selectedVersion?.id ?? null,
        release.name,
        this.impersonatedUserName,
        isGit
      )
      .subscribe((r: any) => {
        if (!r.success) {
          let message = r.status
            ? r.status
            : 'Error with the offline request, please try again later. If the error persist please contact Windriver support';
          // eslint-disable-next-line eqeqeq
          if (r.error == 'ALREADY_IN_PROGRESS_REQUEST') {
            message = 'This request is already in progress.';
          }
          this.dialog.open(ConfirmModalComponent, {
            data: {
              title: 'Failed Offline installation request',
              message,
              confirmButton: 'Close',
              showConfirmButton: true
            }
          });
        } else {
          this.startStatusPolling(release);
        }
        this.requestingOfflineInstaller[release.id] = false;
        this.detector.detectChanges();
      });
  }

  readonly installerStatusLabels: Record<string, string> = {
    QUEUED: 'Offline Installer Requested',
    IN_PROGRESS: 'Creating Offline Installer',
    COMPLETED: 'Offline Installer Ready',
    FAILED: 'Offline Installer Creation Failed',
    EXPIRED: 'Offline Request Expired'
  };

  formatInstallerStatus(status: string): string {
    return this.installerStatusLabels[status] ?? status;
  }

  private getPollingInterval(): number {
    const elapsed = (Date.now() - this.pageOpenTime) / 1000;
    if (elapsed >= 7200) {
      return 60 * 60 * 1000;
    } // 2h+ → every 1 hour
    if (elapsed >= 300) {
      return 5 * 60 * 1000;
    } // 5min+ → every 5 minutes
    if (elapsed >= 120) {
      return 30 * 1000;
    } // 2min+ → every 30 seconds
    return 10 * 1000; // default → every 10 seconds
  }

  startStatusPolling(release: UiRelease): void {
    if (!this.isOfflineEligible(release)) {
      return;
    }
    this.stopStatusPolling(release.cardKey);
    const scheduleNext = () => {
      const interval = this.getPollingInterval();
      this.pollingIntervals.set(
        release.cardKey,
        setTimeout(() => poll(), interval)
      );
    };
    const poll = () => {
      this.productService
        .getDownloadHistory(release.id, this.currentUserService.getUsername(), this.impersonatedUserName)
        .subscribe((history: DownloadHistory[]) => {
          if (!history || history.length === 0) {
            scheduleNext();
            return;
          }
          const latest = history.reduce((a, b) => (a.logId > b.logId ? a : b));
          const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);
          if (new Date(latest.requestDate) < oneDayAgo) {
            scheduleNext();
            return;
          }
          const status = latest.expirationStatus === 'EXPIRED' ? 'EXPIRED' : latest.status;
          this.offlineInstallerStatus.set(release.cardKey, status);
          this.detector.detectChanges();
          if (status === 'COMPLETED' || status === 'EXPIRED' || status === 'FAILED') {
            this.stopStatusPolling(release.cardKey);
          } else {
            scheduleNext();
          }
        });
    };
    poll();
  }

  private stopStatusPolling(cardKey: string): void {
    const existing = this.pollingIntervals.get(cardKey);
    if (existing !== undefined) {
      clearTimeout(existing);
      this.pollingIntervals.delete(cardKey);
    }
  }

  ngOnDestroy(): void {
    this.pollingIntervals.forEach((_interval, cardKey) => this.stopStatusPolling(cardKey));
  }

  openOfflineInstallHistory(event: Event, uiRelease: UiRelease): void {
    const userName = this.currentUserService.getUsername();
    this.dialog.open(OfflineInstallHistoryComponent, {
      data: { uiReleaseId: uiRelease.id, userName, impersonatedUserName: this.impersonatedUserName }
    });

    event.stopPropagation();
  }

  downloadOnlineInstaller(rel: UiRelease, hostEngName: string): void {
    const username = this.currentUserService.getUsername();
    this.loadingOnlineInstaller[rel.cardKey] = true;
    this.loadingOnlineInstallerProgress[rel.cardKey] = 0;

    this.productService
      .getOnlineInstaller(
        username,
        hostEngName,
        rel.releaseName,
        rel.release_eng_name_tag_eng_name ?? null,
        this.impersonatedUserName
      )
      .subscribe(
        (event) => {
          if (event instanceof HttpResponse) {
            // Here we fetch the file content,
            // redirect this content to an anchor
            // and force a click to trigger the download manager of the browser and start the download
            const response = event;
            const url = window.URL.createObjectURL(new Blob([response.body]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute(
              'download',
              this.getFilenameFromContentDisposition(response.headers.get('content-disposition'))
            );
            document.body.appendChild(link);
            link.click();
            this.loadingOnlineInstaller[rel.cardKey] = false;
            this.detector.detectChanges();
          } else if (event.type === HttpEventType.DownloadProgress) {
            // This is an download progress event. Compute and show the % done:
            const percentDone = Math.round((100 * event.loaded) / event.total);
            this.loadingOnlineInstallerProgress[rel.cardKey] = percentDone;
            this.detector.detectChanges();
          }
        },
        (error) => {
          console.log('res part of downloadOnlineInstaller', error);
          this.loadingOnlineInstaller[rel.cardKey] = false;
          this.detector.detectChanges();
        }
      );
  }

  isTruncated(el: HTMLElement): boolean {
    return el.scrollWidth > el.clientWidth;
  }

  isExpired(date: string): boolean {
    const isExpired = new Date() > new Date(date);
    return isExpired;
  }

  private getFilenameFromContentDisposition(disposition: string) {
    let filename = 'default-filename';
    if (disposition && disposition.indexOf('attachment') !== -1) {
      const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
      const matches = filenameRegex.exec(disposition);
      if (matches != null && matches[1]) {
        filename = matches[1].replace(/['"]/g, '');
      }
    }
    return filename;
  }

  openModalImpersonation = (userId) => {
    this.dialog.open(ConfirmModalComponent, {
      data: {
        title: 'Impersonation activated',
        message: `If you have already opened the Wind River Container Registry (Harbor) in your browser, then the entitlement for ${userId} \n will not be updated until you log out of Harbor and click LOGIN VIA OIDC PROVIDER to log in again.`,
        confirmButton: 'OK',
        showConfirmButton: true
      }
    });
  };

  openLicenseInNewTab(release: UiRelease): void {
    const encodedPath = encodeURIComponent(release.license_agreement.url);
    const fullUrl = `${this.apiBackendBaseUrl}/v1/tools/minio-render?path=${encodedPath}`;
    window.open(fullUrl, '_blank');
    // window.open(fullUrl, '_blank', 'noopener,noreferrer');
  }
}
